import React, { useState, useEffect } from "react";
import { getApiUrl } from "../../config";
import { useNavigate } from "react-router-dom";
import { env, STRIPE_CUSTOMER_PORTAL, useGlobalState } from "../../helpers/GlobalStateProvider";
import { openPortalOnClick, redirectToPortal } from "../../helpers/stripePortal";
import {
  Box,
  Card,
  Flex,
  Heading,
  Link,
  SegmentGroup,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Button } from "../../components/ui/button";
import { LuArrowRight, LuCheck } from "react-icons/lu";
import { isTrustedRedirectUrl, safeRedirect } from "../../helpers/validateRedirectUrl";

import { planTiers, defaultStakingTiers, legacyPlanTiers, isLegacyTier, getPlanForTier } from "./planData";
import { PlanCard } from "./PlanCard";
import { StakingPlanCard } from "./StakingPlanCard";
import { SubscriberSection } from "./SubscriberSection";
import type { StakingTierData, PaymentRecord } from "./types";
import { getToken } from "../../helpers/tokenManager";

const APIPlansContainer: React.FC = () => {
  const { info, stakeMorAndConnectWallet } = useGlobalState();
  const navigate = useNavigate();

  const tier = info?.tier || 0;
  const stripeTier = info?.stripeTier || 0;
  const stakedBalance = info?.stakedBalance || 0;
  const billingInterval = (info as any)?.billingInterval || null;
  const isSubscriber = (stripeTier > 0) || !!info?.customerId;
  const onLegacyPlan = isLegacyTier(tier);

  const [planView, setPlanView] = useState<"annual" | "monthly" | "staking">("annual");
  const [tierLinks, setTierLinks] = useState<{ [key: string]: string }>({});
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);

  useEffect(() => {
    const fetchPaymentHistory = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const response = await fetch(`${getApiUrl(env)}/v3/apiKey/payment_history`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: "{}",
        });
        if (!response.ok) return;
        const data = await response.json();
        setPaymentHistory(data?.payments || []);
      } catch {
        // Silently fail
      }
    };
    fetchPaymentHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchTierLinks = async () => {
      try {
        const token = getToken();
        const response = await fetch(`${getApiUrl(env)}/webhooks/buy`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) return;
        const data = await response.json();

        setCustomerId(data.customerId || null);

        if (data.customerId) {
          const links: { [key: string]: string } = {};
          for (let i = 5; i <= 12; i++) links[String(i)] = STRIPE_CUSTOMER_PORTAL;
          setTierLinks(links);
        } else {
          setTierLinks(data.tiers || {});
        }
      } catch {
        // Silently fail for signed-out users
      }
    };
    fetchTierLinks();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // === Plan button helpers ===
  const getTierForPlan = (plan: (typeof planTiers)[number]) =>
    planView === "annual" ? plan.annualTier : plan.monthlyTier;

  const isCurrentCreditCardPlan = (plan: (typeof planTiers)[number]) =>
    tier === plan.monthlyTier || tier === plan.annualTier;

  const getPlanButton = (plan: (typeof planTiers)[number]) => {
    if (isCurrentCreditCardPlan(plan)) return null;

    const planTier = getTierForPlan(plan);
    const isLowerTier = planTier < tier;
    const buttonUrl = tierLinks[String(planTier)];

    // Downgrade goes directly to Stripe portal
    if (isLowerTier) {
      if (!customerId) return null;
      const validButtonUrl = buttonUrl && isTrustedRedirectUrl(buttonUrl) ? buttonUrl : undefined;
      const handleDowngrade = (e: React.MouseEvent) => {
        e.preventDefault();
        const bearerToken = getToken();
        if (!bearerToken) return navigate("/signin");
        if (buttonUrl === STRIPE_CUSTOMER_PORTAL) return void redirectToPortal();
        if (buttonUrl) safeRedirect(buttonUrl);
      };
      return (
        <Button
          asChild={!!validButtonUrl}
          variant="solid"
          colorPalette="gray"
          width="full"
          onClick={validButtonUrl ? undefined : handleDowngrade}
        >
          {validButtonUrl ? (
            <Link
              href={validButtonUrl}
              onClick={(e) => {
                if (validButtonUrl === STRIPE_CUSTOMER_PORTAL) {
                  e.preventDefault();
                  void redirectToPortal();
                }
              }}
            >
              Downgrade Plan
            </Link>
          ) : (
            <>Downgrade Plan</>
          )}
        </Button>
      );
    }

    // Upgrade / new plan — go to Stripe checkout
    const isTrusted = !!buttonUrl && isTrustedRedirectUrl(buttonUrl);
    const handleClick = () => {
      if (!getToken()) return navigate("/signin");
      if (buttonUrl === STRIPE_CUSTOMER_PORTAL) return void redirectToPortal();
      if (isTrusted) safeRedirect(buttonUrl);
    };

    return (
      <Button width="full" onClick={handleClick} variant="solid" colorPalette={plan.recommended ? "teal" : "gray"} disabled={!!getToken() && !isTrusted}>
        Try free for 14 days
        <LuArrowRight />
      </Button>
    );
  };

  // === Staking plan helpers ===
  const backendStakingTiers: StakingTierData[] =
    (info?.stakingTiers && info.stakingTiers.length > 0) ? info.stakingTiers : defaultStakingTiers;

  const isCurrentStakingPlan = (stakingTier: number) => stakingTier === tier;

  const getStakingButton = (plan: StakingTierData) => {
    if (isCurrentStakingPlan(plan.tier)) return null;

    const isLowerTier = plan.tier < tier;
    const difference = plan.minStakedBalance - stakedBalance;
    const isRecommended = plan.tier === 6;

    const handleClick = () => {
      const bearerToken = getToken();
      if (!bearerToken) return navigate("/signin");
      if (difference > 0) stakeMorAndConnectWallet(difference, false);
      else if (difference < 0) stakeMorAndConnectWallet(0, true);
    };

    if (isLowerTier) {
      return (
        <Button variant="solid" colorPalette="gray" width="full" onClick={handleClick}>
          Downgrade Plan
        </Button>
      );
    }

    return (
      <Button width="full" onClick={handleClick} variant="solid" colorPalette={isRecommended ? "teal" : "gray"}>
        Stake MOR for access
        <LuArrowRight />
      </Button>
    );
  };

  // ===== SUBSCRIBER VIEW =====
  const subscriberSection = isSubscriber ? (
    <SubscriberSection
      stripeTier={stripeTier}
      tier={tier}
      onLegacyPlan={onLegacyPlan}
      credits={info?.credits || 0}
      billingInterval={billingInterval}
      paidThrough={info?.paidThrough || null}
      paymentHistory={paymentHistory}
    />
  ) : null;

  // Non-legacy subscribers see only subscription details
  if (isSubscriber && !onLegacyPlan) {
    return subscriberSection;
  }

  // ===== PLAN CARDS =====
  return (
    <Stack gap={6}>
      {subscriberSection}
      {onLegacyPlan && (
        <Card.Root>
          <Card.Header>
            <Heading size="md" fontWeight="bold" fontSize="20px">Current Plan</Heading>
          </Card.Header>
          <Card.Body>
            <Stack gap={4}>
              <Flex justifyContent="space-between" alignItems="center" height="32px">
                <Text fontSize="md" color="#52525b">Plan</Text>
                <Text fontSize="md" fontWeight="bold">{legacyPlanTiers[tier]} (Legacy)</Text>
              </Flex>
              <Flex justifyContent="space-between" alignItems="center" height="32px">
                <Text fontSize="md" color="#52525b">Plan source</Text>
                <Text fontSize="md" fontWeight="bold">
                  {stripeTier > 0 && (info?.stakedTier || 0) > 0
                    ? "Stripe payment + MOR staking"
                    : stripeTier > 0
                    ? "Stripe payment"
                    : (info?.stakedTier || 0) > 0
                    ? "MOR staking"
                    : "Free"}
                </Text>
              </Flex>
              {(info?.stakedTier || 0) > 0 && (
                <Flex justifyContent="space-between" alignItems="center" height="32px">
                  <Text fontSize="md" color="#52525b">MOR staked</Text>
                  <Text fontSize="md" fontWeight="bold">{parseFloat(String(stakedBalance)).toFixed(2)} MOR</Text>
                </Flex>
              )}
              <Flex justifyContent="space-between" alignItems="center" height="32px">
                <Text fontSize="md" color="#52525b">Credit limit</Text>
                <Text fontSize="md" fontWeight="bold">
                  {(backendStakingTiers.find((t) => t.tier === tier)?.credits || 0).toLocaleString()} credits
                </Text>
              </Flex>
              <Text fontSize="xs" color="#a1a1aa">This is a legacy plan. Upgrade to one of our new plans below.</Text>
            </Stack>
          </Card.Body>
          {isSubscriber && (
            <Card.Footer>
              <Button variant="solid" colorPalette="gray" width="full" asChild>
                <Link href={STRIPE_CUSTOMER_PORTAL} target="_blank" rel="noopener noreferrer" onClick={openPortalOnClick}>
                  Manage Subscription <LuArrowRight />
                </Link>
              </Button>
            </Card.Footer>
          )}
        </Card.Root>
      )}

      <Card.Root>
        <Card.Header>
          <Card.Title fontSize="30px" fontWeight="bold" letterSpacing="-0.4px" lineHeight="32px">
            Flexible subscription plans that grow with your business
          </Card.Title>
          <Card.Description fontSize="md" lineHeight="24px" color="#52525b">
            Choose an affordable plan that's ideal for your business. Pay monthly, annually, or stake
            MOR tokens for access. Monthly and annual plans include a 14-day free trial. Earn 15% APR
            on each stake MOR.{" "}
            <Link href="/api-docs/" target="_blank" rel="noopener noreferrer" color="#9333ea" fontWeight="semibold">
              Read more about the CoinCap API.
            </Link>
          </Card.Description>
        </Card.Header>

        <Card.Body>
          <Stack alignItems="center" gap={6}>
            {/* Annual / Monthly / MOR Staking Toggle */}
            <SegmentGroup.Root
              value={planView}
              onValueChange={(e) => setPlanView(e.value as "annual" | "monthly" | "staking")}
              width={{ base: "full", md: "fit-content" }}
              // The recipe defaults to min-width: max-content, which refuses to
              // shrink and overflows narrow mobile cards. Let it shrink to the
              // container on mobile so the labels wrap instead of overflowing.
              minWidth={{ base: "0", md: "auto" }}
              maxWidth="full"
              fontSize={{ base: "xs", md: "sm" }}
            >
              <SegmentGroup.Indicator />
              <SegmentGroup.Item value="annual" minWidth={{ base: "0", md: "160px" }} flex={{ base: "1", md: "initial" }} justifyContent="center">
                <SegmentGroup.ItemText>
                  <Flex alignItems="center" justifyContent="center" gap="4px" flexWrap={{ base: "wrap", md: "nowrap" }}>
                    <Text as="span" fontWeight={400}>Annual</Text>
                    <Text as="span" fontWeight={600} whiteSpace="nowrap">Save 20%</Text>
                  </Flex>
                </SegmentGroup.ItemText>
                <SegmentGroup.ItemHiddenInput />
              </SegmentGroup.Item>
              <SegmentGroup.Item value="monthly" minWidth={{ base: "0", md: "160px" }} flex={{ base: "1", md: "initial" }} justifyContent="center">
                <SegmentGroup.ItemText>Monthly</SegmentGroup.ItemText>
                <SegmentGroup.ItemHiddenInput />
              </SegmentGroup.Item>
              <SegmentGroup.Item value="staking" minWidth={{ base: "0", md: "160px" }} flex={{ base: "1", md: "initial" }} justifyContent="center">
                <SegmentGroup.ItemText whiteSpace={{ base: "normal", md: "nowrap" }} textAlign="center">MOR Staking</SegmentGroup.ItemText>
                <SegmentGroup.ItemHiddenInput />
              </SegmentGroup.Item>
            </SegmentGroup.Root>

            {planView !== "staking" ? (
              <Stack width="full" gap="12px">
                <Flex width="full" gap="12px" flexWrap="wrap">
                  {planTiers.map((plan) => (
                    <PlanCard
                      key={plan.monthlyTier}
                      plan={plan}
                      planView={planView === "annual" ? "annual" : "monthly"}
                      isCurrentPlan={isCurrentCreditCardPlan(plan)}
                      button={getPlanButton(plan)}
                    />
                  ))}
                </Flex>
              </Stack>
            ) : (
              /* ===== STAKING VIEW ===== */
              <Stack width="full" gap="12px">
                <Flex width="full" gap="12px" flexWrap="wrap">
                  {backendStakingTiers.map((plan, idx) => {
                    const matchingPlan = planTiers[idx];
                    // TODO(overage-credits): restore overageText wiring — see projects/pro/docs/overage-credits-scope.md
                    // const overageText = matchingPlan?.features[1]?.text;
                    const button = getStakingButton(plan);
                    return (
                      <StakingPlanCard
                        key={plan.tier}
                        plan={plan}
                        matchingPlanDescription={matchingPlan?.description}
                        // TODO(overage-credits): restore — overageText={overageText}
                        isCurrentPlan={isCurrentStakingPlan(plan.tier)}
                        button={button}
                      />
                    );
                  })}
                </Flex>
              </Stack>
            )}

            {/* "Not ready to commit yet?" Section */}
            <Card.Root width="full">
              <Card.Body gap={2}>
                <Text fontSize="xl" fontWeight="bold" lineHeight="30px">
                  Not ready to commit yet? Demo our API with no obligation.
                </Text>
                <Text fontSize="sm" color="#52525b" lineHeight="20px">
                  Perfect for testing the API. Not designed for production applications. Just create an API key above to get started.
                </Text>
                <Flex gap={8} flexWrap="wrap" alignItems="flex-start">
                  <Flex alignItems="flex-start" gap="6px">
                    <LuCheck style={{ flexShrink: 0, width: 24, height: 24 }} color="#2dd4bf" />
                    <Text fontSize="sm" color="#52525b" lineHeight="20px">500 credits/mo*</Text>
                  </Flex>
                  <Flex alignItems="flex-start" gap="6px">
                    <LuCheck style={{ flexShrink: 0, width: 24, height: 24 }} color="#2dd4bf" />
                    <Text fontSize="sm" color="#52525b" lineHeight="20px">5 requests/minute</Text>
                  </Flex>
                  <Flex alignItems="flex-start" gap="6px">
                    <LuCheck style={{ flexShrink: 0, width: 24, height: 24 }} color="#2dd4bf" />
                    <Text fontSize="sm" color="#52525b" lineHeight="20px">Standard REST endpoints ONLY</Text>
                  </Flex>
                </Flex>
              </Card.Body>
            </Card.Root>
          </Stack>
        </Card.Body>

        {/* Footer Notes */}
        <Card.Footer flexDir="column" alignItems="flex-start" color="#a1a1aa" fontSize="sm" lineHeight="20px" gap={0}>
          <Text>
            * Each API call charges a base cost of 1 credit, then 1 additional credit per 2,500 bytes of data.
            Web-socket charges 1 credit per minute of use.
          </Text>
          {/* TODO(overage-credits): restore — see projects/pro/docs/overage-credits-scope.md */}
          {/* <Text>
            ** Overage charges are added to the following month's invoice. Annual plans are billed for overages on a monthly basis.
          </Text> */}
        </Card.Footer>
      </Card.Root>

    </Stack>
  );
};

export default APIPlansContainer;
